import { useEffect, useRef, useState } from 'react';
import { SPEECH_RATE_VALUES, formatSpeechRateLabel } from '@orborix/voice-client';
import { IconSpeed, IconXSmall, IconChevron } from '../icons.js';

/**
 * Pill compacto de velocidad del coach para el header. `circular` muestra solo
 * el label (p.ej. `1×`) sin íconos flanqueantes → queda como un círculo sutil
 * a la izquierda de los filtros (pedido de chat3: header más bajo).
 *
 * Cableado a los valores REALES del package (`SPEECH_RATE_VALUES`, 0.7–1.2);
 * los labels (`0,5×`…`1,5×`) salen de `formatSpeechRateLabel` — no inventamos
 * números nuevos. El cambio aplica al próximo turno del coach.
 *
 * Tri-state (plan 38 F1): `value` es la pref (null = auto por nivel); `effective`
 * es la velocidad que realmente se usa (pref ?? recomendada por nivel). El chip
 * muestra la efectiva; el popup deja elegir "Auto" o una velocidad fija.
 */
export function SpeedQuickControl({
  value,
  effective,
  onChange,
  anchor = 'right',
  circular = false,
  disabled = false,
}: {
  value: number | null;
  effective: number;
  onChange: (next: number | null) => void;
  anchor?: 'left' | 'right';
  circular?: boolean;
  /** Sesión terminada: el cambio de velocidad viaja por WS → sería un no-op. */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent | TouchEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open]);

  const isAuto = value == null;
  const label = formatSpeechRateLabel(effective);
  // Resaltamos cuando la velocidad efectiva no es la natural (1×) — cubre tanto
  // una elección manual como el auto que baja a "0,75×" para el principiante.
  const tweaked = Math.abs(effective - 1) > 0.001;

  return (
    <div className={`speed-quick-wrap anchor-${anchor}`} ref={wrapRef}>
      <button
        className={`speed-quick-chip ${circular ? 'is-circular' : ''} ${open ? 'is-open' : ''} ${tweaked ? 'is-tweaked' : ''} ${isAuto ? 'is-auto' : ''}`}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label={`Velocidad del coach: ${label}${isAuto ? ' (auto por nivel)' : ''}`}
        title={`Velocidad · ${label}${isAuto ? ' · auto' : ''}`}
      >
        {!circular && <IconSpeed size={13} />}
        <span className="speed-quick-val">{label}</span>
        {!circular && <IconChevron size={9} />}
      </button>

      {open && (
        <div className="speed-quick-pop slide-up" onClick={(e) => e.stopPropagation()}>
          <div className="speed-quick-pop-arrow" />
          <div className="speed-quick-pop-head">
            <span className="tag" style={{ color: 'var(--lime)' }}>
              VELOCIDAD DEL COACH
            </span>
            <button className="speed-quick-pop-x" onClick={() => setOpen(false)} aria-label="Cerrar">
              <IconXSmall size={11} />
            </button>
          </div>
          <div className="speed-quick-pop-row">
            <button
              className={`speed-quick-opt ${isAuto ? 'is-active' : ''}`}
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              Auto
            </button>
            {SPEECH_RATE_VALUES.map((s) => {
              const active = !isAuto && Math.abs(s - effective) < 0.001;
              return (
                <button
                  key={s}
                  className={`speed-quick-opt ${active ? 'is-active' : ''}`}
                  onClick={() => {
                    onChange(s);
                    setOpen(false);
                  }}
                >
                  {formatSpeechRateLabel(s)}
                </button>
              );
            })}
          </div>
          <div className="speed-quick-pop-foot">
            <span className="script" style={{ color: 'var(--lime)', fontSize: 14 }}>
              Tip:
            </span>
            <span>
              {' '}
              <b>Auto</b> ajusta la velocidad a tu nivel. Bajá a 0,5× para entender mejor.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
